import { Icon, type IconName } from "../icons/icon";
import { cn } from "../../lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Slot } from "radix-ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip/tooltip";

function getIconButtonAriaLabel(
  ariaLabel: React.AriaAttributes["aria-label"],
  tooltipContent: React.ReactNode,
) {
  if (ariaLabel?.trim()) {
    return ariaLabel;
  }

  if (typeof tooltipContent === "string" && tooltipContent.trim()) {
    return tooltipContent;
  }

  throw new Error(
    "IconButton requires a non-empty aria-label when tooltipContent is not a string.",
  );
}

const iconButtonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md transition-[color,box-shadow,opacity] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50 data-[focus]:ring-[3px] data-[focus]:ring-ring/50 data-[loading]:cursor-wait data-[pressed]:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 data-[hover]:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 data-[hover]:bg-destructive/90 data-[focus]:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:data-[focus]:ring-destructive/40",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground data-[hover]:bg-accent data-[hover]:text-accent-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground data-[hover]:bg-accent data-[hover]:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline data-[hover]:underline",
      },
      size: {
        default: "size-8",
        sm: "size-7",
        xs: "size-6",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "default",
    },
  },
);

type IconButtonAccessibleName =
  | { "aria-label"?: string; tooltipContent: string }
  | { "aria-label": string; tooltipContent: React.ReactNode };

export type IconButtonProps = Omit<
  React.ComponentProps<"button">,
  "aria-label" | "children"
> &
  VariantProps<typeof iconButtonVariants> &
  IconButtonAccessibleName & {
    asChild?: boolean;
    children?: React.ReactNode;
    icon: IconName;
    loading?: boolean;
  };

function IconButton({
  "aria-label": ariaLabelProp,
  asChild = false,
  children,
  className,
  disabled,
  icon,
  loading = false,
  onClickCapture,
  ref,
  size,
  tooltipContent,
  type = "button",
  variant,
  ...props
}: IconButtonProps) {
  const isDisabled = disabled || loading;
  const ariaLabel = getIconButtonAriaLabel(ariaLabelProp, tooltipContent);
  const commonProps = {
    "aria-busy": loading || undefined,
    "aria-label": ariaLabel,
    "data-loading": loading || undefined,
    "data-slot": "icon-button",
    className: cn(iconButtonVariants({ variant, size, className })),
  };
  const iconContent = (
    <Icon
      src={loading ? "LoaderCircle" : icon}
      className={loading ? "animate-spin" : undefined}
    />
  );
  const Comp = asChild ? Slot.Root : "button";

  const button = asChild ? (
    <Comp
      {...commonProps}
      ref={ref}
      aria-disabled={isDisabled || undefined}
      onClickCapture={(event) => {
        onClickCapture?.(event);

        if (isDisabled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      {...props}
    >
      {iconContent}
      <Slot.Slottable>{children}</Slot.Slottable>
    </Comp>
  ) : (
    <Comp
      {...commonProps}
      ref={ref}
      disabled={isDisabled}
      type={type}
      onClickCapture={onClickCapture}
      {...props}
    >
      {iconContent}
    </Comp>
  );

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          {isDisabled ? (
            <span data-slot="icon-button-wrapper" className="inline-flex">
              {button}
            </span>
          ) : (
            button
          )}
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </>
  );
}

export { IconButton, iconButtonVariants };
